/**
 * Alert Component - Alert banner
 * Pattern based on github-admin alertBanner styles
 */

const React = (globalThis as any).React;
const { useEffect } = React;

import { styles } from '../styles';

export interface AlertProps {
  message: string;
  type?: 'error' | 'warning' | 'info' | 'success';
  closable?: boolean;
  onClose?: () => void;
  icon?: React.ReactNode;
}

export function Alert({
  message,
  type = 'info',
  closable = false,
  onClose,
  icon,
}: AlertProps) {
  // WCAG: Keyboard dismiss (Escape key)
  useEffect(() => {
    if (!closable || !onClose) return;
    
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [closable, onClose]);
  const typeStyles = {
    error: styles.alertBannerCritical,
    warning: styles.alertBannerWarning,
    info: styles.alertBannerInfo,
    success: styles.alertBannerSuccess,
  };
  
  const alertStyle: React.CSSProperties = {
    ...styles.alertBanner,
    ...typeStyles[type],
  };
  
  const icons = {
    error: '⚠️',
    warning: '⚠️',
    info: 'ℹ️',
    success: '✅',
  };
  
  // WCAG: ARIA live region for dynamic content
  return React.createElement('div', {
    style: alertStyle,
    role: 'alert',
    'aria-live': type === 'error' ? 'assertive' : 'polite',
    'aria-atomic': 'true',
  },
    icon || React.createElement('span', { style: { fontSize: '18px' } }, icons[type]),
    React.createElement('span', { style: { flex: 1 } }, message),
    closable && onClose && React.createElement('button', {
      onClick: onClose,
      onKeyDown: (e: any) => {
        // WCAG: Keyboard dismiss (Escape key)
        if (e.key === 'Escape' && onClose) {
          e.preventDefault();
          onClose();
        }
      },
      style: {
        background: 'transparent',
        border: 'none',
        color: 'inherit',
        cursor: 'pointer',
        padding: '4px',
        fontSize: '18px',
        lineHeight: 1,
        opacity: 0.7,
        borderRadius: '4px',
        transition: 'opacity 0.2s',
      },
      onMouseEnter: (e: any) => {
        e.currentTarget.style.opacity = '1';
      },
      onMouseLeave: (e: any) => {
        e.currentTarget.style.opacity = '0.7';
      },
      'aria-label': 'Close alert',
      tabIndex: 0,
    }, '×')
  );
}

